import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  Col,
  Row,
  UncontrolledTooltip,
  Button,
} from 'reactstrap';
import Input from '~/components/Inputs/Input';
import IconButton from '~/components/IconButton';
import InputCheckbox from '~/components/Inputs/InputCheckbox';
import InputSelect from '~/components/Inputs/InputSelect';
import { gatewayOptions } from '~/util/filters';
import TwoFactorRolesConfig from './TwoFactorRolesConfig';

export default function ConfigurationCard({
  config,
  index,
  handleSubmit,
  className,
}) {
  const formRef = useRef(null);
  const formRefMobile = useRef(null);

  const [editModeOn, setEditModeOn] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(
    gatewayOptions.find(option => option.value === config.value) || null
  );

  const isGatewayConfig = config.name === 'payment_gateway';
  const isBooleanConfig = config.type === 'boolean';
  const TOGGLE_CONFIGS = {
    delivery_types: {
      title: 'Modalidades habilitadas',
      minOneMessage:
        'Pelo menos uma modalidade de entrega deve permanecer habilitada.',
      labels: {
        delivery: 'Entrega',
        store_pickup: 'Retirada em Loja',
        correios: 'Correios',
        malinha: 'Malinha',
      },
    },
    payment_types: {
      title: 'Formas de pagamento habilitadas',
      minOneMessage:
        'Pelo menos uma forma de pagamento deve permanecer habilitada.',
      labels: {
        link: 'Link / Pix',
        pos: 'Maquineta / POS',
        money: 'Dinheiro',
        paid: 'Pedido Pago',
      },
    },
  };

  const toggleConfig = TOGGLE_CONFIGS[config.name] || null;
  const isToggleConfig = Boolean(toggleConfig);
  const TOGGLE_LABELS = toggleConfig?.labels || {};
  const isManualEditConfig = !isBooleanConfig && !isGatewayConfig;

  // Chave ausente ou valor inválido = habilitado (mesmo fallback do backend)
  const parseToggleConfigValue = value => {
    let parsed = value;

    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (err) {
        parsed = null;
      }
    }

    const result = {};

    Object.keys(TOGGLE_LABELS).forEach(key => {
      result[key] = parsed?.[key] !== false;
    });

    return result;
  };

  const [toggleConfigDraft, setToggleConfigDraft] = useState(
    parseToggleConfigValue(config.value)
  );

  useEffect(() => {
    setToggleConfigDraft(parseToggleConfigValue(config.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.value]);

  useEffect(() => {
    setSelectedGateway(
      gatewayOptions.find(option => option.value === config.value) || null
    );
  }, [config.value]);

  const displayValue = useMemo(() => {
    if (isGatewayConfig) {
      return selectedGateway?.label || config.value || 'Não informado';
    }

    if (isBooleanConfig) {
      return config.value ? 'Ativado' : 'Desativado';
    }

    if (
      config.value === null ||
      typeof config.value === 'undefined' ||
      config.value === ''
    ) {
      return 'Não informado';
    }

    if (isToggleConfig) {
      const parsed = parseToggleConfigValue(config.value);
      const enabledLabels = Object.keys(TOGGLE_LABELS)
        .filter(key => parsed[key])
        .map(key => TOGGLE_LABELS[key]);

      return enabledLabels.length
        ? enabledLabels.join(', ')
        : 'Nenhuma modalidade habilitada';
    }

    // Fallback genérico para configurações json/objeto futuras
    if (typeof config.value === 'object') {
      return JSON.stringify(config.value);
    }

    return String(config.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.value,
    isBooleanConfig,
    isGatewayConfig,
    isToggleConfig,
    selectedGateway,
  ]);

  const valueColorClass = useMemo(() => {
    if (!isBooleanConfig) return 'text-gray-700';

    return config.value ? 'text-success' : 'text-muted';
  }, [config.value, isBooleanConfig]);

  const resetForms = () => {
    formRef.current?.setErrors({});
    formRefMobile.current?.setErrors({});
    formRef.current?.setData(config);
    formRefMobile.current?.setData(config);
  };

  const openEditMode = () => {
    resetForms();
    setEditModeOn(true);
  };

  const closeEditMode = () => {
    resetForms();
    setToggleConfigDraft(parseToggleConfigValue(config.value));
    setEditModeOn(false);
  };

  const submitDesktopForm = () => {
    formRef.current?.submitForm();
  };

  const submitMobileForm = () => {
    formRefMobile.current?.submitForm();
  };

  const handleFormSubmit = data => {
    if (isGatewayConfig) {
      handleSubmit(data, config, index);
      return;
    }

    setEditModeOn(false);
    handleSubmit(data, config, index);
  };

  const isTwoFactorActive =
    config.name === 'two_factor_enabled' &&
    (config.value === true ||
      config.value === 'true' ||
      config.value === '1' ||
      config.value === 1);

  const renderManualEditActions = submitHandler => (
    <Row className="w-100 mx-0 mt-3">
      <Col className="px-0 d-flex justify-content-end flex-wrap">
        <Button
          type="button"
          color="primary"
          outline
          className="new-layout-btn mr-2 mb-2"
          onClick={closeEditMode}
        >
          Cancelar
        </Button>

        <Button
          type="button"
          color="primary"
          className="new-layout-btn main mb-2"
          onClick={submitHandler}
        >
          Salvar
        </Button>
      </Col>
    </Row>
  );

  const toggleConfigDraftKey = key => {
    setToggleConfigDraft(draft => ({
      ...draft,
      [key]: !draft[key],
    }));
  };

  const submitToggleConfig = () => {
    const hasEnabled = Object.values(toggleConfigDraft).some(Boolean);

    if (!hasEnabled) {
      toast.error(
        toggleConfig?.minOneMessage ||
          'Pelo menos uma opção deve permanecer habilitada.'
      );
      return;
    }

    setEditModeOn(false);
    handleSubmit({ value: toggleConfigDraft }, config, index);
  };

  const renderToggleConfigEditor = suffix => (
    <>
      <div className="font-size-14 font-weight-700 text-gray-700 mb-3">
        {toggleConfig?.title}
      </div>

      {Object.keys(TOGGLE_LABELS).map(key => (
        <div className="d-flex align-items-center mb-3" key={key}>
          <span
            className="font-size-13 text-gray-700"
            style={{ minWidth: '160px' }}
          >
            {TOGGLE_LABELS[key]}
          </span>

          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id={`delivery-type-${key}-${config.id}-${suffix}`}
              checked={toggleConfigDraft[key]}
              onChange={() => toggleConfigDraftKey(key)}
            />
            <label
              className="custom-control-label"
              htmlFor={`delivery-type-${key}-${config.id}-${suffix}`}
              aria-label={`Alternar modalidade ${TOGGLE_LABELS[key]}`}
            />
          </div>
        </div>
      ))}

      {renderManualEditActions(submitToggleConfig)}
    </>
  );

  const renderDesktopInlineControl = () => {
    if (isBooleanConfig) {
      return (
        <Form
          ref={formRef}
          onSubmit={handleFormSubmit}
          initialData={config}
          className="mt-2"
        >
          <InputCheckbox
            id={`config-boolean-${config.id}-desktop`}
            name="value"
            checked={config.value}
            configurationCard
            onChange={() => {
              formRef.current.submitForm();
            }}
          />
          <button
            type="submit"
            className="d-none"
            aria-label={`Salvar configuração ${config.name} desktop`}
          />
        </Form>
      );
    }

    if (isGatewayConfig) {
      return (
        <Form
          ref={formRef}
          onSubmit={handleFormSubmit}
          initialData={config}
          className="mt-2"
        >
          <InputSelect
            name="option"
            className="common-select"
            options={gatewayOptions}
            onChange={selectedOption => {
              setSelectedGateway(selectedOption);
              formRef.current.submitForm();
            }}
            value={selectedGateway}
            placeholder="Gateway switch"
            singlePath
          />
          <button
            type="submit"
            className="d-none"
            aria-label={`Salvar gateway ${config.name} desktop`}
          />
        </Form>
      );
    }

    return null;
  };

  const renderMobileInlineControl = () => {
    if (isBooleanConfig) {
      return (
        <Form
          ref={formRefMobile}
          onSubmit={handleFormSubmit}
          initialData={config}
          className="mt-2"
        >
          <InputCheckbox
            id={`config-boolean-${config.id}-mobile`}
            name="value"
            checked={config.value}
            configurationCard
            onChange={() => {
              formRefMobile.current.submitForm();
            }}
          />
          <button
            type="submit"
            className="d-none"
            aria-label={`Salvar configuração ${config.name} mobile`}
          />
        </Form>
      );
    }

    if (isGatewayConfig) {
      return (
        <Form
          ref={formRefMobile}
          onSubmit={handleFormSubmit}
          initialData={config}
          className="mt-2"
        >
          <InputSelect
            name="option"
            className="common-select"
            options={gatewayOptions}
            onChange={selectedOption => {
              setSelectedGateway(selectedOption);
              formRefMobile.current.submitForm();
            }}
            value={selectedGateway}
            placeholder="Gateway switch"
            singlePath
          />
          <button
            type="submit"
            className="d-none"
            aria-label={`Salvar gateway ${config.name} mobile`}
          />
        </Form>
      );
    }

    return null;
  };

  return (
    <>
      <Card
        className={`card new-layout table-card mb-4 ${
          config.border_class || ''
        } ${className || ''} d-none d-lg-block`}
      >
        <CardBody className="card-body new-layout px-3 py-4">
          <Row className="w-100 mx-0 align-items-start">
            <Col xs={5} className="px-0 pr-3">
              <div className="new-layout-card-list-strong-value d-flex flex-wrap word-break-break-all">
                {config.name}
              </div>
              <div className="new-layout-card-list-value mt-1 text-muted font-size-12">
                {config.description}
              </div>
            </Col>

            <Col xs={4} className="px-0 pr-3">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Valor atual
              </div>

              <div
                className={`font-size-12 font-weight-600 ${valueColorClass} word-break-break-all`}
              >
                {displayValue}
              </div>

              {(isBooleanConfig || isGatewayConfig) &&
                renderDesktopInlineControl()}
            </Col>

            <Col xs={2} className="px-0 pr-2">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Tipo
              </div>
              <div className="new-layout-card-list-value w-100 word-break-break-all">
                {config.type || 'Não informado'}
              </div>
            </Col>

            <Col xs={1} className="px-0 d-flex justify-content-end">
              {isManualEditConfig ? (
                <>
                  {!editModeOn ? (
                    <div className="d-flex align-items-center">
                      {config.name === 'tracksale_campaign_code' && (
                        <>
                          <i
                            id={`config-${config.id}-comma-tip`}
                            className="las la-info-circle font-size-18 mr-2"
                          />
                          <UncontrolledTooltip
                            placement="top"
                            target={`config-${config.id}-comma-tip`}
                          >
                            Essa configuração deve ser preenchida com números
                            separados por vírgulas. Exemplo: 8,2,4
                          </UncontrolledTooltip>
                        </>
                      )}

                      <IconButton
                        icon="las la-pen"
                        color="secondary"
                        id={`edit-btn-${config.id}`}
                        tip="Editar"
                        click={openEditMode}
                      />
                    </div>
                  ) : (
                    <IconButton
                      icon="las la-times"
                      color="danger"
                      id={`cancel-btn-${config.id}`}
                      tip="Cancelar edição"
                      click={closeEditMode}
                    />
                  )}
                </>
              ) : (
                <>
                  <i
                    id={`config-${config.id}-no-actions`}
                    className="las la-info-circle font-size-18"
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target={`config-${config.id}-no-actions`}
                  >
                    Essa configuração não é editável da mesma forma que as
                    demais.
                  </UncontrolledTooltip>
                </>
              )}
            </Col>
          </Row>

          {editModeOn && isManualEditConfig && (
            <div className="border-top mt-4 pt-4">
              {isToggleConfig ? (
                renderToggleConfigEditor('desktop')
              ) : (
                <Form
                  ref={formRef}
                  onSubmit={handleFormSubmit}
                  initialData={config}
                  className="w-100"
                >
                  <Row className="w-100 mx-0">
                    <Col xs={12} className="px-0">
                      <Input
                        name="value"
                        placeholder={
                          config.type === 'array'
                            ? 'Ex.: valor1, valor2, valor3'
                            : 'Valor da configuração'
                        }
                      />
                    </Col>
                  </Row>

                  {renderManualEditActions(submitDesktopForm)}

                  <button
                    type="submit"
                    className="d-none"
                    aria-label={`Salvar configuração ${config.name} desktop`}
                  />
                </Form>
              )}
            </div>
          )}

          {isTwoFactorActive && (
            <div className="border-top mt-4 pt-3">
              <TwoFactorRolesConfig />
            </div>
          )}
        </CardBody>
      </Card>

      <Card
        className={`card new-layout list-card store d-lg-none mb-4 ${
          className || ''
        }`}
      >
        <CardBody className="card-body new-layout px-3 py-3">
          <Row className="w-100 mx-0 align-items-start">
            <Col xs={10} className="px-0">
              <div className="font-size-14 font-weight-700 text-gray-700">
                {config.name}
              </div>
              <div className="text-muted font-size-12 mt-1">
                {config.description}
              </div>
            </Col>

            <Col xs={2} className="px-0 d-flex justify-content-end">
              {isManualEditConfig ? (
                <>
                  {!editModeOn ? (
                    <IconButton
                      icon="las la-pen"
                      color="secondary"
                      id={`edit-btn-mobile-${config.id}`}
                      tip="Editar"
                      click={openEditMode}
                    />
                  ) : (
                    <IconButton
                      icon="las la-times"
                      color="danger"
                      id={`cancel-btn-mobile-${config.id}`}
                      tip="Cancelar edição"
                      click={closeEditMode}
                    />
                  )}
                </>
              ) : (
                <>
                  <i
                    id={`config-${config.id}-no-actions-mobile`}
                    className="las la-info-circle font-size-18"
                  />
                  <UncontrolledTooltip
                    placement="top"
                    target={`config-${config.id}-no-actions-mobile`}
                  >
                    Essa configuração não é editável da mesma forma que as
                    demais.
                  </UncontrolledTooltip>
                </>
              )}
            </Col>
          </Row>

          <Row className="w-100 mx-0 mt-3">
            <Col xs={12} className="px-0 mb-2">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Valor atual
              </div>

              <div
                className={`font-size-12 font-weight-600 ${valueColorClass} word-break-break-all`}
              >
                {displayValue}
              </div>

              {(isBooleanConfig || isGatewayConfig) &&
                renderMobileInlineControl()}
            </Col>

            <Col xs={12} className="px-0">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Tipo
              </div>
              <div className="font-size-12 text-gray-700">
                {config.type || 'Não informado'}
              </div>
            </Col>
          </Row>

          {editModeOn && isManualEditConfig && (
            <div className="border-top mt-3 pt-3">
              {isToggleConfig ? (
                renderToggleConfigEditor('mobile')
              ) : (
                <Form
                  ref={formRefMobile}
                  onSubmit={handleFormSubmit}
                  initialData={config}
                >
                  <Row className="w-100 mx-0">
                    <Col xs={12} className="px-0">
                      <Input
                        name="value"
                        placeholder={
                          config.type === 'array'
                            ? 'Ex.: valor1, valor2, valor3'
                            : 'Valor da configuração'
                        }
                      />
                    </Col>
                  </Row>

                  {renderManualEditActions(submitMobileForm)}

                  <button
                    type="submit"
                    className="d-none"
                    aria-label={`Salvar configuração ${config.name} mobile`}
                  />
                </Form>
              )}
            </div>
          )}

          {isTwoFactorActive && (
            <div className="border-top mt-3 pt-3">
              <TwoFactorRolesConfig />
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
